import React from 'react';
import { Image, StyleSheet, View } from 'react-native';
import { colors, spacing } from '../config/theme';

// «Якобы» brand marks. Raster assets are generated by brand/make-brand-icons.mjs
// and brand/make-ornaments.mjs from the vector logo — regenerate after a logo
// change. Kept as PNGs so we don't take a runtime SVG dependency.

const YAKOBY_INK = require('../../assets/brand/yakoby-ink.png');
const WORDMARK_INK = require('../../assets/brand/wordmark-ink.png');
const STAR = require('../../assets/brand/ornament/star-ink.png');
const STRIP = require('../../assets/brand/ornament/strip-ink.png');

// Monoline brand pictograms (assets/brand/ic/*.png, generated by
// brand/make-pictograms.mjs). Referenced by name from content/guide.js.
const PICTOS = {
  hello: require('../../assets/brand/ic/hello.png'),
  flame: require('../../assets/brand/ic/flame.png'),
  calendar: require('../../assets/brand/ic/calendar.png'),
  handshake: require('../../assets/brand/ic/handshake.png'),
  chat: require('../../assets/brand/ic/chat.png'),
  grid: require('../../assets/brand/ic/grid.png'),
  checklist: require('../../assets/brand/ic/checklist.png'),
  people: require('../../assets/brand/ic/people.png'),
  shop: require('../../assets/brand/ic/shop.png'),
  chart: require('../../assets/brand/ic/chart.png'),
  book: require('../../assets/brand/ic/book.png'),
  cup: require('../../assets/brand/ic/cup.png'),
  clock: require('../../assets/brand/ic/clock.png'),
  bell: require('../../assets/brand/ic/bell.png'),
};

export function Pictogram({ name, size = 24, color = colors.textPrimary, style }) {
  const src = PICTOS[name] || PICTOS.book;
  return (
    <Image
      source={src}
      resizeMode="contain"
      style={[{ width: size, height: size, tintColor: color }, style]}
    />
  );
}

// Brand stickers (assets/stickers/, sliced by brand/make-stickers.mjs). Only a
// curated few are bundled — used sparingly as accents, not wallpaper.
const STICKERS = {
  matreshka: require('../../assets/stickers/folk/matreshka.png'),
  samovar: require('../../assets/stickers/folk/samovar.png'),
  kiosk: require('../../assets/stickers/folk/kiosk.png'),
  bufet: require('../../assets/stickers/folk/bufet.png'),
  pechka: require('../../assets/stickers/folk/pechka.png'),
  valenki: require('../../assets/stickers/folk/valenki.png'),
  gzhel: require('../../assets/stickers/folk/gzhel.png'),
  voron: require('../../assets/stickers/folk/voron.png'),
  samovar2: require('../../assets/stickers/folk/samovar.png'),
  lastochka: require('../../assets/stickers/tattoo/lastochka.png'),
  karty: require('../../assets/stickers/tattoo/karty.png'),
};
const STICKER_POOL = ['matreshka', 'samovar', 'kiosk', 'bufet', 'gzhel', 'voron', 'lastochka'];

export function Sticker({ name, size = 76, style }) {
  const src = STICKERS[name] || STICKERS.matreshka;
  return <Image source={src} resizeMode="contain" style={[{ width: size, height: size }, style]} />;
}

// Stable pick from a seed string, so a given screen always shows the same one.
export function pickSticker(seed = '') {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = Math.abs((h * 31 + seed.charCodeAt(i)) % 2147483647);
  return STICKER_POOL[h % STICKER_POOL.length];
}

// Scattered stickers behind a screen's content — "обои". Big and clearly
// visible; they hug the edges so content stays readable. Fixed layout (no
// animation), pointer-transparent, deterministic per seed.
const BACKDROP_SPOTS = [
  { top: '1%', left: '-15%', size: 300, rot: '-14deg' },
  { top: '15%', right: '-17%', size: 340, rot: '12deg' },
  { top: '41%', left: '-19%', size: 320, rot: '7deg' },
  { top: '63%', right: '-16%', size: 290, rot: '-9deg' },
  { bottom: '2%', left: '-13%', size: 300, rot: '10deg' },
  { bottom: '-9%', right: '17%', size: 240, rot: '16deg' },
];
export function StickerBackdrop({ seed = 'x', opacity = 0.16 }) {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = Math.abs((h * 31 + seed.charCodeAt(i)) % 2147483647);
  return (
    <View pointerEvents="none" style={s.backdrop}>
      {BACKDROP_SPOTS.map((sp, i) => {
        const name = STICKER_POOL[(h + i * 3) % STICKER_POOL.length];
        const { size, rot, ...pos } = sp;
        return (
          <Image
            key={i}
            source={STICKERS[name]}
            resizeMode="contain"
            style={[{ position: 'absolute', width: size, height: size, opacity, transform: [{ rotate: rot }] }, pos]}
          />
        );
      })}
    </View>
  );
}

// ЯКОБЫ logotype (just the top word). `full` swaps in the ЯКОБЫ / КОФЕ·ТАБАК
// lockup. Colour is applied with tintColor so one asset serves every surface.
export function Wordmark({ height = 26, color = colors.textPrimary, full = false, style }) {
  const src = full ? WORDMARK_INK : YAKOBY_INK;
  const ratio = full ? 1.575 : 2.344; // asset aspect (w/h)
  return (
    <Image
      source={src}
      resizeMode="contain"
      style={[{ height, width: height * ratio, tintColor: color }, style]}
    />
  );
}

// Alatyr — folk 8-point star. Decorative only; keep it quiet.
export function FolkStar({ size = 120, color = colors.textPrimary, opacity = 0.09, style }) {
  return (
    <Image
      source={STAR}
      resizeMode="contain"
      pointerEvents="none"
      style={[{ width: size, height: size, tintColor: color, opacity }, style]}
    />
  );
}

// Broadsheet rule: two hairlines with a run of diamonds. Full-bleed divider.
export function FolkStrip({ color = colors.borderStrong, opacity = 0.5, style }) {
  return (
    <View style={[s.stripWrap, style]}>
      <Image
        source={STRIP}
        resizeMode="repeat"
        style={{ width: '100%', height: 14, tintColor: color, opacity }}
      />
    </View>
  );
}

const s = StyleSheet.create({
  stripWrap: { marginVertical: spacing.sm, alignItems: 'stretch' },
  backdrop: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden' },
});
